import {
  createContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useContext,
} from "react";

/**
 * Factory function to create a ticket context and related hooks
 * 
 * Creates a standardized context for ticket-type modules with:
 * - List of items with automatic fetching and caching
 * - Single item retrieval with cache-first strategy
 * - Optional secondary data (like pricing for T&M)
 * - Refresh capability with cache invalidation
 * - reportNewItem(id): silent refresh + brief green highlight on the new row/card (call after create/clone)
 * - highlightAddedItemId: current id to highlight (consumed by TicketLogTable)
 * 
 * @example
 * // In your module's context file:
 * import { createTicketContext } from '../../features/tickets';
 * import { getRFIs, getRFIById } from '../services/api/rfi';
 * 
 * export const { 
 *   TicketContext: RFIContext, 
 *   useTicketContext: useRFIContext,
 *   useTicketCtx: useRFICtx 
 * } = createTicketContext({
 *   name: 'RFI',
 *   fetchAll: getRFIs,
 *   fetchById: getRFIById,
 * });
 * 
 * @param {Object} options Configuration options
 * @param {string} options.name - Module name (for error messages)
 * @param {Function} options.fetchAll - API function to fetch all items
 * @param {Function} options.fetchById - API function to fetch single item by ID
 * @param {Function} [options.fetchSecondary] - Optional API function for secondary data (e.g., pricing)
 * @param {string} [options.secondaryKey] - Key name for secondary data in list items
 * @returns {Object} Object containing Context, useContext hook, and context hook
 */
export function createTicketContext({
  name,
  fetchAll,
  fetchById,
  fetchSecondary = null,
  secondaryKey = null,
}) {
  // Create the context
  const TicketContext = createContext(null);

  // Create the useContext hook with error boundary
  function useTicketContext() {
    const context = useContext(TicketContext);
    if (!context) {
      throw new Error(
        `use${name}Context must be used within a ${name}Provider`
      );
    }
    return context;
  }

  const HIGHLIGHT_DURATION_MS = 2000;

  // Create the context value hook (to be used in the Provider)
  function useTicketCtx() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [itemCache, setItemCache] = useState({});
    const [secondaryCache, setSecondaryCache] = useState({});
    const [highlightAddedItemId, setHighlightAddedItemId] = useState(null);
    const [highlightDeletedItemId, setHighlightDeletedItemId] = useState(null);

    // Fetch all items
    const fetchItems = useCallback(async () => {
      try {
        setLoading(true);
        const data = await fetchAll();
        setItems(data);
        return data;
      } catch (error) {
        console.error(`Error fetching ${name} items:`, error);
        return [];
      } finally {
        setLoading(false);
      }
    }, []);

    // Load items on mount
    useEffect(() => {
      fetchItems();
    }, [fetchItems]);

    // Refresh items (call after mutations).
    // Options: { silent: true } refetches without setting loading state (better UX for clone/quick updates).
    const refreshItems = useCallback(async (options = {}) => {
      const { silent = false } = options;
      try {
        if (!silent) setLoading(true);
        const data = await fetchAll();
        setItems(data);
        setItemCache({});
        setSecondaryCache({});
        return data;
      } catch (error) {
        console.error(`Error fetching ${name} items:`, error);
        return [];
      } finally {
        if (!silent) setLoading(false);
      }
    }, []);

    // Report a newly added item: silent refresh + brief highlight flash on the list.
    // Call after create or clone so the list updates and the new row/card is highlighted.
    const reportNewItem = useCallback(
      async (id) => {
        if (id == null) return;
        await refreshItems({ silent: true });
        setHighlightAddedItemId(id);
        setTimeout(() => setHighlightAddedItemId(null), HIGHLIGHT_DURATION_MS);
      },
      [refreshItems]
    );

    // Confirm delete flow: flash row red, call delete API, wait for animation, then silent refresh.
    const executeDelete = useCallback(
      async (item, deleteApiFn) => {
        if (!item?.id || typeof deleteApiFn !== "function") return;
        setHighlightDeletedItemId(item.id);
        const deleteFlashMs = 1000;
        try {
          await deleteApiFn(item);
          await new Promise((r) => setTimeout(r, deleteFlashMs));
          await refreshItems({ silent: true });
        } finally {
          setTimeout(() => setHighlightDeletedItemId(null), deleteFlashMs);
        }
      },
      [refreshItems]
    );

    // Get item by ID - use cache or fetch
    const getItemById = useCallback(
      async (id) => {
        // Check cache first
        if (itemCache[id]) {
          return itemCache[id];
        }

        // Check if it's in the items list
        const itemInList = items.find((item) => item.id === parseInt(id));
        if (itemInList) {
          setItemCache((prev) => ({ ...prev, [id]: itemInList }));
          return itemInList;
        }

        // Fetch from API
        try {
          const data = await fetchById(id);
          setItemCache((prev) => ({ ...prev, [id]: data }));
          return data;
        } catch (error) {
          console.error(`Error fetching ${name} item ${id}:`, error);
          throw error;
        }
      },
      [items, itemCache]
    );

    // Get secondary data by item ID - use cache or fetch (if configured)
    const getSecondaryById = useCallback(
      async (itemId) => {
        if (!fetchSecondary) {
          throw new Error(`${name} context does not have secondary data configured`);
        }

        // Check cache first
        if (secondaryCache[itemId]) {
          return secondaryCache[itemId];
        }

        // Check if the item in list has secondary data
        if (secondaryKey) {
          const itemInList = items.find((item) => item.id === parseInt(itemId));
          if (itemInList?.[secondaryKey]) {
            setSecondaryCache((prev) => ({
              ...prev,
              [itemId]: itemInList[secondaryKey],
            }));
            return itemInList[secondaryKey];
          }
        }

        // Fetch from API
        try {
          const data = await fetchSecondary(itemId);
          setSecondaryCache((prev) => ({ ...prev, [itemId]: data }));
          return data;
        } catch (error) {
          // No secondary data exists - this may be expected
          throw error;
        }
      },
      [items, secondaryCache]
    );

    // Build context value
    const contextValue = useMemo(() => {
      const baseValue = {
        items,
        loading,
        refreshItems,
        reportNewItem,
        highlightAddedItemId,
        highlightDeletedItemId,
        executeDelete,
        getItemById,
      };

      // Add secondary data getter if configured
      if (fetchSecondary) {
        baseValue.getSecondaryById = getSecondaryById;
      }

      return baseValue;
    }, [items, loading, refreshItems, reportNewItem, highlightAddedItemId, highlightDeletedItemId, executeDelete, getItemById, getSecondaryById]);

    return contextValue;
  }

  return {
    TicketContext,
    useTicketContext,
    useTicketCtx,
  };
}

export default createTicketContext;
